import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { AlertTriangle, Sparkles } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ConceptGraph } from '@/features/knowledge/components/ConceptGraph'
import { categoryColor } from '@/features/knowledge/components/categoryStyle'
import type { ConceptRole, ConciergeResult, Turn, TurnConcept } from '../api/picassoApi'
import { MODE_ICON, MODE_LABEL, messageOf } from './modes'


const ROLE_LABEL: Record<ConceptRole, string> = {
  matched: 'matched the query',
  direction: 'related direction',
  facet: 'facet of the results',
  lane: 'walk lane',
}

const ms = (v: number | null | undefined) => (v == null ? '—' : v < 1000 ? `${Math.round(v)} ms` : `${(v / 1000).toFixed(1)} s`)

function ResultCard({ r, seed }: { r: ConciergeResult; seed: boolean }) {
  const image = r.mockupHero || r.image
  const body = (
    <>
      <div className="relative aspect-square overflow-hidden rounded border bg-slate-100">
        {image ? <img src={image} alt={r.title} loading="lazy" className="h-full w-full object-cover transition group-hover:scale-105" /> : null}
        <span className="absolute left-1 top-1 rounded bg-white/90 px-1.5 text-[10px] font-medium tabular-nums text-slate-700">#{r.position}</span>
        {seed && <span className="absolute right-1 top-1 rounded bg-violet-600 px-1.5 text-[10px] font-medium text-white" title="A vector match the graph walk started from">seed</span>}
      </div>
      <div className="mt-1 truncate text-xs font-medium">{r.title}</div>
      <div className="flex items-center justify-between gap-1 text-[11px] text-slate-500">
        <span className="truncate">{r.artist || '—'}</span>
        {r.score != null && r.score > 0 && <span className="tabular-nums">{r.score.toFixed(2)}</span>}
      </div>
    </>
  )
  return (
    <div className="group">
      {r.inAms ? <Link to={`/app/artworks/${r.id}`}>{body}</Link> : <a href={r.url} target="_blank" rel="noreferrer">{body}</a>}
      {r.lane && <div className="mt-0.5 truncate text-[10px] text-violet-700">lane: {r.lane}</div>}
      {r.why?.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {r.why.slice(0, 3).map((w) => <span key={w} className="rounded bg-slate-100 px-1 py-0.5 text-[10px] text-slate-600">{w}</span>)}
        </div>
      )}
      {!r.inAms && <div className="mt-0.5 text-[10px] text-amber-700">not an AMS artwork id</div>}
    </div>
  )
}

function Results({ turn }: { turn: Turn }) {
  const seeds = new Set((turn.response?.knowledge?.seeds ?? []).map((s) => String(s.id)))
  if (!turn.results.length) return <p className="py-8 text-center text-sm text-slate-500">{turn.error ? 'No results — the call failed.' : 'The concierge returned no artworks.'}</p>
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-6">
      {turn.results.map((r) => <ResultCard key={`${r.lane ?? ''}-${r.id}-${r.position}`} r={r} seed={seeds.has(String(r.id))} />)}
    </div>
  )
}

function ConceptTable({ concepts }: { concepts: TurnConcept[] }) {
  if (!concepts.length) return <p className="text-sm text-slate-500">This answer names no concepts (a lexical or artist lookup, or a tool without a graph summary).</p>
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Concept the concierge used</TableHead>
            <TableHead>How</TableHead>
            <TableHead className="text-right">Similarity</TableHead>
            <TableHead>In our knowledge graph</TableHead>
            <TableHead className="text-right">Artworks</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {concepts.map((c) => (
            <TableRow key={`${c.role}-${c.name}`}>
              <TableCell>
                <span className="font-medium">{c.name}</span>
                {c.kind && <span className="ml-1.5 text-[11px] text-slate-400">{c.kind}</span>}
              </TableCell>
              <TableCell className="text-xs text-slate-600">{ROLE_LABEL[c.role]}{c.decided ? ` · ${c.decided}` : ''}</TableCell>
              <TableCell className="text-right tabular-nums">{c.similarity != null ? c.similarity.toFixed(2) : '—'}</TableCell>
              <TableCell>
                {c.ucid ? (
                  <Link to={`/app/knowledge/${c.ucid}`} className="inline-flex items-center gap-1.5 text-xs hover:underline">
                    <span className="h-2 w-2 rounded-full" style={{ background: categoryColor(c.category) }} />
                    {c.kgName}
                    <span className="text-slate-400">{c.category ?? 'uncategorised'}</span>
                    {c.weight != null && <span className="text-slate-400">· weight {c.weight.toFixed(2)}</span>}
                  </Link>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                    <AlertTriangle className="h-3 w-3" /> no concept of ours by this name
                  </span>
                )}
              </TableCell>
              <TableCell className="text-right tabular-nums">{(c.kgArtworks ?? c.artworks ?? c.worksHere ?? c.freq ?? 0).toLocaleString()}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function GraphTab({ turn }: { turn: Turn }) {
  const graph = useMemo(() => {
    const images = new Map(turn.results.map((r) => [String(r.id), r.image]))
    return { ...turn.graph, nodes: turn.graph.nodes.map((n) => ({ ...n, imageUrl: images.get(String(n.artworkId)) })) }
  }, [turn])
  const unlinked = turn.concepts.filter((c) => !c.ucid).length
  return (
    <div className="space-y-4">
      {turn.graph.nodes.length > 1 ? (
        <>
          <ConceptGraph graph={graph} height={520} labelAll />
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-600">
            <span><b>Centre</b>: the query</span>
            <span><b>Circles</b>: concepts it reached, coloured by our category (grey: not in our graph), sized by artworks</span>
            <span><b>Thick links</b>: matched the query (by similarity) · thinner: related directions and facets</span>
            <span><b>Thumbnails</b>: results, linked to the concepts our graph says they carry — or to the query when none</span>
          </div>
        </>
      ) : (
        <p className="py-8 text-center text-sm text-slate-500">Nothing to draw for this turn.</p>
      )}
      {unlinked > 0 && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {unlinked} concept{unlinked === 1 ? '' : 's'} the concierge used {unlinked === 1 ? 'has' : 'have'} no concept of ours by that name. The concierge
          reads the nightly build; a concept missing here may be one the build dropped, merged under another name, or a facet from the 2024 graph.
        </p>
      )}
      <ConceptTable concepts={turn.concepts} />
    </div>
  )
}

/** Key/value rows for whatever object the concierge put in its trace (plan, subgraph, …). */
function KV({ value }: { value: unknown }) {
  if (value == null) return <span className="text-slate-400">—</span>
  if (typeof value !== 'object') return <span className="font-mono text-xs">{String(value)}</span>
  if (Array.isArray(value)) {
    if (!value.length) return <span className="text-slate-400">none</span>
    if (value.every((v) => typeof v !== 'object')) return <span className="font-mono text-xs">{value.join(', ')}</span>
  }
  return <pre className="max-h-64 overflow-auto rounded bg-slate-50 p-2 text-[11px] leading-snug">{JSON.stringify(value, null, 2)}</pre>
}

function TraceTab({ turn }: { turn: Turn }) {
  const r = turn.response ?? {}
  const k = r.knowledge ?? {}
  const rows: Array<[string, unknown, string?]> = [
    ['Mode', turn.mode === 'tool' ? `MCP tool · ${turn.tool}` : MODE_LABEL[turn.mode]],
    ['Latency', ms(turn.latencyMs), 'round trip from AMS, including the written message'],
    ['Search id', turn.searchId, 'joins this search to the concierge’s telemetry (Shopper searches shows the same log)'],
    ['Backend asked for', (turn.request as { backend?: string }).backend ?? 'default (the concierge’s configured retriever)'],
    ['Lookup', r.intent ? `${r.intent.type}: ${r.intent.label}` : null, 'set when the lexical router answered an explicit artist or artwork lookup'],
    ['Seed mode', k.seedMode, 'how retrieval started: vector seeds, concepts, the subgraph walk…'],
    ['Subgraph', k.subgraph, 'route, whether it expanded, the decision, pool size and edges walked'],
    ['Plan', k.plan, 'the query plan the template planner chose'],
    ['Graph add-ons', k.addons],
    ['Seeds', k.seeds?.map((s) => `${s.title} (${s.id})`)],
    ['Personalization', r.personalization],
    ['Results', `${turn.results.length} returned${r.meta?.total != null ? ` of ${r.meta.total}` : ''}`],
  ]
  return (
    <div className="divide-y rounded-md border">
      {rows.map(([label, value, hint]) => (
        <div key={label} className="grid gap-2 px-3 py-2 text-sm sm:grid-cols-[11rem_1fr]">
          <div>
            <div className="text-slate-700">{label}</div>
            {hint && <div className="text-[11px] leading-snug text-slate-400">{hint}</div>}
          </div>
          <div className="min-w-0"><KV value={value} /></div>
        </div>
      ))}
    </div>
  )
}

function RawTab({ turn }: { turn: Turn }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {[['Request', turn.request], ['Response', turn.response ?? { error: turn.error }]].map(([label, v]) => (
        <div key={label as string} className="min-w-0 space-y-1">
          <div className="text-xs font-medium text-slate-500">{label as string}</div>
          <pre className="max-h-[560px] overflow-auto rounded-md border bg-slate-50 p-3 text-[11px] leading-snug">{JSON.stringify(v, null, 2)}</pre>
        </div>
      ))}
    </div>
  )
}

const TABS = ['results', 'graph', 'trace', 'raw'] as const
type Tab = (typeof TABS)[number]
const TAB_LABEL: Record<Tab, string> = { results: 'Results', graph: 'Graph', trace: 'Trace', raw: 'Raw' }

/** One turn: what was asked, the concierge's answer, and the ways to look inside it. */
export function TurnView({ turn, onFollowUp, busy }: { turn: Turn; onFollowUp: (q: string) => void; busy: boolean }) {
  const [tab, setTab] = useState<Tab>('results')
  const Icon = MODE_ICON[turn.mode]
  const message = messageOf(turn)
  const followUps = turn.response?.followUps ?? []
  const counts: Record<Tab, number | null> = { results: turn.results.length, graph: turn.concepts.length, trace: null, raw: null }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Icon className="h-3.5 w-3.5" /> {turn.mode === 'tool' ? turn.tool : MODE_LABEL[turn.mode]}
            <span>· {formatDistanceToNow(new Date(turn.createdAt), { addSuffix: true })}</span>
            <span>· {ms(turn.latencyMs)}</span>
            {turn.response?.knowledge?.seedMode && <Badge variant="outline" className="text-[10px] font-normal">{turn.response.knowledge.seedMode}</Badge>}
          </div>
          <div className="mt-1 break-words text-lg font-medium">{turn.input}</div>
        </div>
      </div>

      {turn.error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{turn.error}</div>
      )}

      {message && (
        <div className="flex gap-3 rounded-md border bg-slate-50 p-3 text-sm text-slate-800">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-violet-600" />
          <p className="whitespace-pre-line">{message}</p>
        </div>
      )}

      {followUps.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-sm">
          <span className="text-xs text-slate-500">Suggested next:</span>
          {followUps.map((f) => (
            <button key={f} disabled={busy} onClick={() => onFollowUp(f)} className="rounded-full border bg-white px-2.5 py-0.5 text-xs hover:bg-slate-50 disabled:opacity-50">
              {f}
            </button>
          ))}
        </div>
      )}

      <div className="flex gap-1 border-b">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            aria-pressed={tab === t}
            className={`-mb-px border-b-2 px-3 py-1.5 text-sm ${tab === t ? 'border-slate-900 font-medium' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
          >
            {TAB_LABEL[t]}
            {counts[t] != null && <span className="ml-1.5 text-xs tabular-nums text-slate-400">{counts[t]}</span>}
          </button>
        ))}
      </div>
      {tab === 'results' && <Results turn={turn} />}
      {tab === 'graph' && <GraphTab turn={turn} />}
      {tab === 'trace' && <TraceTab turn={turn} />}
      {tab === 'raw' && <RawTab turn={turn} />}
    </div>
  )
}
